import React, { useRef, useState, useEffect } from "react";
import styled from "styled-components";
import { useFormContext } from "react-hook-form";
import { COLOR_RED, COLOR_PRIMARY, COLOR_GRAY, COLOR_LIGHT_GRAY } from "../../constants/color";
import { CloseIcon } from "./icons/CloseIcon";

interface Props {
  name: string;
  label?: string;
  required?: boolean;
  value?: File[];
  onChange?: (files: File[]) => void;
  error?: string;
  helperText?: string;
  fullWidth?: boolean;
  disabled?: boolean;
  maxFiles?: number;
  existingImages?: (string | File)[];
  onExistingImagesChange?: (deletedIndices: number[]) => void;
  deletedIndices?: number[];
}

export const MultiImageUpload: React.FC<Props> = ({
  name,
  label,
  required,
  value = [],
  onChange,
  error,
  helperText,
  fullWidth,
  disabled,
  maxFiles = 10,
  existingImages = [],
  onExistingImagesChange,
  deletedIndices: externalDeletedIndices = [],
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [previews, setPreviews] = useState<string[]>([]);
  const [internalDeletedIndices, setInternalDeletedIndices] = useState<number[]>([]);
  const formContext = useFormContext();
  const { setValue } = formContext ?? {};

  // Use external deleted indices if provided, otherwise use internal state
  const deletedIndices = externalDeletedIndices.length > 0 ? externalDeletedIndices : internalDeletedIndices;

  // Reset deletedIndices when existingImages changes significantly (parent component updates)
  useEffect(() => {
    if (existingImages.length === 0 && deletedIndices.length > 0) {
      setInternalDeletedIndices([]);
    }
  }, [existingImages.length, deletedIndices.length]);

  // Reset deletedIndices when component first mounts with existingImages
  useEffect(() => {
    if (existingImages.length > 0 && internalDeletedIndices.length === 0) {
      // Component is being initialized, ensure deletedIndices is empty
      setInternalDeletedIndices([]);
    }
  }, [existingImages]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    const imageFiles = files.filter(file => file.type.startsWith("image/"));

    if (imageFiles.length === 0) return;

    // Check if adding these files would exceed maxFiles
    const totalFiles = value.length + (existingImages.length - deletedIndices.length) + imageFiles.length;
    if (totalFiles > maxFiles) {
      alert(`You can only upload up to ${maxFiles} images.`);
      return;
    }

    const newFiles = [...value, ...imageFiles];

    // Generate previews for new files
    const newPreviews: string[] = [];
    imageFiles.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        newPreviews.push(reader.result as string);
        if (newPreviews.length === imageFiles.length) {
          setPreviews([...previews, ...newPreviews]);
        }
      };
      reader.readAsDataURL(file);
    });

    if (onChange) onChange(newFiles);
    setValue?.(name, newFiles, { shouldValidate: true });

    // Reset input value to allow selecting the same file again
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const handleRemoveImage = (index: number, isExisting: boolean) => {
    if (isExisting) {
      // Remove existing image - add to deleted indices
      const newDeletedIndices = [...deletedIndices, index];

      // If we're using external deleted indices, update through callback
      if (externalDeletedIndices.length > 0 || onExistingImagesChange) {
        if (onExistingImagesChange) {
          onExistingImagesChange(newDeletedIndices);
        }
      } else {
        // Otherwise update internal state
        setInternalDeletedIndices(newDeletedIndices);
      }

    } else {
      // Remove new file
      // console.log('Removing new file at index:', index);
      const newFiles = value.filter((_, i) => i !== index);
      const newPreviews = previews.filter((_, i) => i !== index);

      setPreviews(newPreviews);
      if (onChange) onChange(newFiles);
      setValue?.(name, newFiles, { shouldValidate: true });
    }
  };

  // Generate previews when value changes from outside (edit mode)
  useEffect(() => {
    if (value && value.length > 0) {
      const newPreviews: string[] = [];
      value.forEach((file, index) => {
        if (file instanceof File) {
          const reader = new FileReader();
          reader.onloadend = () => {
            newPreviews[index] = reader.result as string;
            if (newPreviews.filter(Boolean).length === value.length) {
              setPreviews(newPreviews);
            }
          };
          reader.readAsDataURL(file);
        }
      });
    } else {
      setPreviews([]);
    }
  }, [value]);

  // Debug effect to track state changes
  // useEffect(() => {
  //   console.log('MultiImageUpload: State changed:', {
  //     existingImages: existingImages.length,
  //     deletedIndices,
  //     value: value.length
  //   });
  // }, [existingImages, deletedIndices, value]);

  // Calculate total image count excluding deleted images
  const totalImageCount = value.length + (existingImages.length - deletedIndices.length);

  // console.log('MultiImageUpload debug:', {
  //   existingImages: existingImages.length,
  //   deletedIndices,
  //   deletedIndicesLength: deletedIndices.length,
  //   value: value.length,
  //   valueLength: value.length,
  //   calculation: `${value.length} + (${existingImages.length} - ${deletedIndices.length})`,
  //   totalImageCount,
  //   maxFiles
  // });

  return (
    <StyledContainer $fullWidth={fullWidth}>
      {label && (
        <StyledLabel>
          {label} {required && <StyledRequired>*</StyledRequired>}
        </StyledLabel>
      )}

      <StyledInputWrapper>
        <StyledInput
          type="file"
          accept="image/*"
          multiple
          id={name}
          ref={inputRef}
          onChange={handleFileChange}
          disabled={disabled}
        />
        <StyledButton
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={disabled || totalImageCount >= maxFiles}
        >
          Choose Images ({totalImageCount}/{maxFiles})
        </StyledButton>
      </StyledInputWrapper>

      {(previews.length > 0 || existingImages.length > 0) && (
        <StyledPreviewContainer>
          {/* Show existing images that are not deleted */}
          {existingImages.map((imageItem, index) => {
            // Skip deleted images
            if (deletedIndices.includes(index)) {
              // console.log(`Skipping deleted image at index ${index}`);
              return null;
            }

            // console.log(`Showing existing image at index ${index}`);

            // Build proper image URL like ImageUpload component does
            const buildImageUrl = (imagePath: string): string => {
              if (!imagePath) return '';

              // Priority 0: if it's already an absolute URL, return it as is
              if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
                return imagePath;
              }

              // Priority 1: Use dedicated media URL environment variable if available
              if (process.env.REACT_APP_MEDIA_URL) {
                const mediaUrl = process.env.REACT_APP_MEDIA_URL.endsWith('/')
                  ? process.env.REACT_APP_MEDIA_URL.slice(0, -1)
                  : process.env.REACT_APP_MEDIA_URL;
                return `${mediaUrl}${imagePath.startsWith('/') ? imagePath : `/${imagePath}`}`;
              }

              // Priority 2: Use API base URL and construct media URL (works in production)
              const apiBaseUrl = process.env.REACT_APP_API_BASE || '';
              if (apiBaseUrl) {
                try {
                  const url = new URL(apiBaseUrl);
                  const mediaBaseUrl = `${url.protocol}//${url.host}`;
                  // Ensure imagePath starts with / for proper URL construction
                  const normalizedPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
                  return `${mediaBaseUrl}${normalizedPath}`;
                } catch (error) {
                  console.warn('Invalid API base URL, falling back to relative path');
                }
              }

              // Priority 3: Fallback to relative path (works if frontend and media are on same domain)
              return imagePath;
            };

            const imageUrl = typeof imageItem === 'string' ? buildImageUrl(imageItem) : URL.createObjectURL(imageItem);
            return (
              <StyledImageWrapper key={`existing-${index}`}>
                <StyledPreview src={imageUrl} alt={`Existing ${index + 1}`} />
                <StyledRemoveButton
                  type="button"
                  onClick={() => handleRemoveImage(index, true)}
                  disabled={disabled}
                >
                  <CloseIcon />
                </StyledRemoveButton>
              </StyledImageWrapper>
            );
          })}
          {/* Show new file previews */}
          {previews.map((preview, index) => (
            <StyledImageWrapper key={`new-${index}`}>
              <StyledPreview src={preview} alt={`Preview ${index + 1}`} />
              <StyledRemoveButton
                type="button"
                onClick={() => handleRemoveImage(index, false)}
                disabled={disabled}
              >
                <CloseIcon />
              </StyledRemoveButton>
            </StyledImageWrapper>
          ))}
        </StyledPreviewContainer>
      )}

      {helperText && <StyledHelperText>{helperText}</StyledHelperText>}
      {error && <StyledErrorText>{error}</StyledErrorText>}
    </StyledContainer>
  );
};

const StyledContainer = styled.div<{ $fullWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  ${({ $fullWidth }) => $fullWidth && "width: 100%;"}
`;

const StyledLabel = styled.label`
  font-size: 14px;
  font-weight: 400;
  margin-bottom: 2px;
`;

const StyledRequired = styled.span`
  color: ${COLOR_RED};
`;

const StyledInputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const StyledInput = styled.input`
  display: none;
`;

const StyledButton = styled.button`
  padding: 8px 16px;
  border-radius: 6px;
  border: 1px solid ${COLOR_PRIMARY};
  background: #fff;
  color: ${COLOR_PRIMARY};
  font-size: 14px;
  cursor: pointer;
  transition: background 0.2s;
  
  &:hover:not(:disabled) {
    background: ${COLOR_PRIMARY};
    color: #fff;
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const StyledPreviewContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 8px;
`;

const StyledImageWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const StyledPreview = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 8px;
  border: 1px solid #eee;
  object-fit: cover;
`;

const StyledRemoveButton = styled.button`
  position: absolute;
  top: -6px;
  right: -6px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: none;
  background: ${COLOR_LIGHT_GRAY};
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  transition: background 0.2s;
  z-index: 10;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  
  &:hover:not(:disabled) {
    background: ${COLOR_GRAY};
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  svg {
    width: 12px;
    height: 12px;
  }
`;

const StyledHelperText = styled.div`
  font-size: 12px;
  color: #888;
`;

const StyledErrorText = styled.div`
  font-size: 13px;
  color: ${COLOR_RED};
  margin-top: 2px;
`; 